import Observability from '@launchdarkly/observability';
import type { ObserveOptions } from '@launchdarkly/observability';
import SessionReplay, { LDRecord } from '@launchdarkly/session-replay';
import type { RecordOptions } from '@launchdarkly/session-replay';
import { initialize, type LDClient, type LDPlugin } from 'launchdarkly-js-client-sdk';
import { createClientLogger } from '@/client/logging/client-logger';
import { publicClientConfig } from '@/config/public-client-config';
import { AUTH_TOKEN_PARAM } from '@/constants/auth';
import { sanitizeForLog } from '@/shared/logging/sanitize-log';
import { buildIncidentFingerprint } from '@/shared/observability/incident-policy';
import {
    createLaunchDarklyIncidentSuppressionStore,
    reserveLaunchDarklyIncidentReporting,
} from '@/shared/observability/error-reporting';
import { releaseManifest } from '@/shared/release-manifest';

const logger = createClientLogger({ scope: 'launchdarkly-observability' });
const LD_SERVICE_NAME = 'marketplace-reviews-web';

let launchDarklyBrowserClient: LDClient | null | undefined;
let launchDarklyBrowserObserveHandle: BrowserObserve | null = null;
let hasLoggedDisabledWarning = false;
const browserIncidentSuppressionStore = createLaunchDarklyIncidentSuppressionStore();

type ObserveSpan = {
    addEvent?: (name: string, attributes?: Record<string, unknown>) => unknown;
    setAttribute?: (name: string, value: unknown) => unknown;
    setAttributes?: (attributes: Record<string, unknown>) => unknown;
};

type ObserveMetric = {
    name: string;
    value?: number;
    attributes?: Record<string, unknown>;
};

type BrowserObserve = {
    start?: () => Promise<void> | void;
    startSpan?: (...args: unknown[]) => unknown;
    startManualSpan?: (...args: unknown[]) => unknown;
    recordGauge?: (metric: ObserveMetric) => unknown;
    recordCount?: (metric: ObserveMetric) => unknown;
    recordIncr?: (metric: ObserveMetric) => unknown;
    recordHistogram?: (metric: ObserveMetric) => unknown;
    recordUpDownCounter?: (metric: ObserveMetric) => unknown;
    _recordErrorMessage?: (payload: Record<string, unknown>, metadata?: Record<string, unknown>) => unknown;
    __marketplaceUrlRedactionInstalled?: boolean;
};

type ObservePluginHandle = {
    observe?: BrowserObserve;
};

type ReplayRecordHandle = {
    _marketplaceUrlRedactionInstalled?: boolean;
    _sendPayload?: (...args: unknown[]) => Promise<unknown> | unknown;
    addSessionProperties?: (...args: unknown[]) => Promise<unknown> | unknown;
    events?: unknown[];
    getSession?: (...args: unknown[]) => Promise<unknown> | unknown;
};

const logDisabledWarningOnce = (message: string, metadata?: Record<string, unknown>) => {
    if (hasLoggedDisabledWarning) {
        return;
    }

    hasLoggedDisabledWarning = true;
    logger.warn(message, metadata);
};

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
};

export const sanitizeObservedUrl = (value: string): string => {
    const sanitized = sanitizeForLog(value, { redactSensitiveKeys: false });
    return typeof sanitized === 'string' ? sanitized : String(sanitized ?? '');
};

const containsSensitiveAuthToken = (value: string): boolean =>
    value.toLowerCase().includes(`${AUTH_TOKEN_PARAM.toLowerCase()}=`);

const shouldSanitizeObservedAttribute = (key: string, value: string): boolean => {
    const normalizedKey = key.toLowerCase();

    return (
        normalizedKey.includes('url') ||
        normalizedKey.includes('location') ||
        normalizedKey === 'group' ||
        containsSensitiveAuthToken(value)
    );
};

const sanitizeObservedAttributeValue = (key: string, value: unknown): unknown => {
    if (typeof value === 'string') {
        return shouldSanitizeObservedAttribute(key, value) ? sanitizeObservedUrl(value) : value;
    }

    if (Array.isArray(value)) {
        return value.map((entry) => {
            if (typeof entry !== 'string') {
                return entry;
            }

            return shouldSanitizeObservedAttribute(key, entry) ? sanitizeObservedUrl(entry) : entry;
        });
    }

    return value;
};

const sanitizeObservedAttributes = (
    attributes: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
    if (!attributes) {
        return attributes;
    }

    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(attributes)) {
        sanitized[key] = sanitizeObservedAttributeValue(key, value);
    }

    return sanitized;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (Object.prototype.toString.call(value) !== '[object Object]') {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === null || prototype === Object.prototype;
};

const shouldSanitizeReplayValue = (key: string | undefined, value: string): boolean =>
    key ? shouldSanitizeObservedAttribute(key, value) : containsSensitiveAuthToken(value);

const sanitizeReplayPayloadValue = (value: unknown, seen = new WeakMap<object, unknown>(), key?: string): unknown => {
    if (typeof value === 'string') {
        return shouldSanitizeReplayValue(key, value) ? sanitizeObservedUrl(value) : value;
    }

    if (Array.isArray(value)) {
        return value.map((entry) => sanitizeReplayPayloadValue(entry, seen, key));
    }

    if (!isPlainObject(value)) {
        return value;
    }

    const existing = seen.get(value);
    if (existing) {
        return existing;
    }

    const sanitized: Record<string, unknown> = {};
    seen.set(value, sanitized);

    for (const [key, entry] of Object.entries(value)) {
        sanitized[key] = sanitizeReplayPayloadValue(entry, seen, key);
    }

    return sanitized;
};

const installReplayUrlRedaction = () => {
    const replayRecord = LDRecord as ReplayRecordHandle;
    if (replayRecord._marketplaceUrlRedactionInstalled) {
        return;
    }

    replayRecord._marketplaceUrlRedactionInstalled = true;

    if (typeof replayRecord._sendPayload === 'function') {
        const originalSendPayload = replayRecord._sendPayload;
        replayRecord._sendPayload = function (this: ReplayRecordHandle, ...args: unknown[]) {
            if (Array.isArray(this.events)) {
                this.events = sanitizeReplayPayloadValue(this.events) as unknown[];
            }

            return originalSendPayload.apply(this, args);
        };
    }

    if (typeof replayRecord.addSessionProperties === 'function') {
        const originalAddSessionProperties = replayRecord.addSessionProperties;
        replayRecord.addSessionProperties = function (this: ReplayRecordHandle, ...args: unknown[]) {
            if (args.length === 0) {
                return originalAddSessionProperties.apply(this, args);
            }

            const [properties, ...restArgs] = args;
            return originalAddSessionProperties.apply(this, [sanitizeReplayPayloadValue(properties), ...restArgs]);
        };
    }

    if (typeof replayRecord.getSession === 'function') {
        const originalGetSession = replayRecord.getSession;
        replayRecord.getSession = function (this: ReplayRecordHandle, ...args: unknown[]) {
            const result = originalGetSession.apply(this, args);
            if (result && typeof (result as Promise<unknown>).then === 'function') {
                return (result as Promise<unknown>).then((session) => sanitizeReplayPayloadValue(session));
            }

            return sanitizeReplayPayloadValue(result);
        };
    }
};

const wrapObservedSpan = (span: ObserveSpan | undefined): ObserveSpan | undefined => {
    if (!span) {
        return span;
    }

    return new Proxy(span, {
        get(target, property, receiver) {
            if (property === 'setAttribute' && typeof target.setAttribute === 'function') {
                return (name: string, value: unknown) =>
                    target.setAttribute?.(name, sanitizeObservedAttributeValue(name, value));
            }

            if (property === 'setAttributes' && typeof target.setAttributes === 'function') {
                return (attributes: Record<string, unknown>) =>
                    target.setAttributes?.(sanitizeObservedAttributes(attributes) ?? {});
            }

            if (property === 'addEvent' && typeof target.addEvent === 'function') {
                return (name: string, attributes?: Record<string, unknown>) =>
                    target.addEvent?.(name, sanitizeObservedAttributes(attributes));
            }

            return Reflect.get(target, property, receiver);
        },
    });
};

const wrapObservedSpanCallback = (value: unknown): unknown => {
    if (typeof value !== 'function') {
        return value;
    }

    return (span?: ObserveSpan, ...rest: unknown[]) =>
        (value as (span?: ObserveSpan, ...rest: unknown[]) => unknown)(wrapObservedSpan(span), ...rest);
};

const wrapObservedSpanStarter = (
    observe: BrowserObserve,
    starter: BrowserObserve['startSpan'],
): BrowserObserve['startSpan'] => {
    if (typeof starter !== 'function') {
        return starter;
    }

    return (...args: unknown[]) =>
        starter.call(observe, ...args.map((arg, index) => (index === 0 ? arg : wrapObservedSpanCallback(arg))));
};

const wrapObservedMetricRecorder = <T extends BrowserObserve['recordGauge']>(
    observe: BrowserObserve,
    recorder: T,
): T => {
    if (typeof recorder !== 'function') {
        return recorder;
    }

    return ((metric: ObserveMetric) =>
        recorder.call(observe, {
            ...metric,
            attributes: sanitizeObservedAttributes(metric.attributes),
        })) as unknown as T;
};

const installObserveUrlRedaction = (observe: BrowserObserve | undefined) => {
    if (!observe || observe.__marketplaceUrlRedactionInstalled) {
        return;
    }

    observe.__marketplaceUrlRedactionInstalled = true;
    observe.startSpan = wrapObservedSpanStarter(observe, observe.startSpan);
    observe.startManualSpan = wrapObservedSpanStarter(observe, observe.startManualSpan);
    observe.recordGauge = wrapObservedMetricRecorder(observe, observe.recordGauge);
    observe.recordCount = wrapObservedMetricRecorder(observe, observe.recordCount);
    observe.recordIncr = wrapObservedMetricRecorder(observe, observe.recordIncr);
    observe.recordHistogram = wrapObservedMetricRecorder(observe, observe.recordHistogram);
    observe.recordUpDownCounter = wrapObservedMetricRecorder(observe, observe.recordUpDownCounter);

    if (typeof observe._recordErrorMessage === 'function') {
        const recordErrorMessage = observe._recordErrorMessage;
        observe._recordErrorMessage = (payload, metadata) =>
            recordErrorMessage.call(
                observe,
                sanitizeObservedAttributes(payload) ?? payload,
                sanitizeObservedAttributes(metadata),
            );
    }
};

const startObservedCapture = (plugin: ObservePluginHandle, clientId: string) => {
    const observe = plugin.observe;
    installObserveUrlRedaction(observe);
    launchDarklyBrowserObserveHandle = observe ?? null;

    if (typeof observe?.start !== 'function') {
        logger.warn(
            '[client.runtime-observability.launchdarkly-bootstrap] LaunchDarkly browser observability did not expose a start handle; browser capture remains disabled',
            {
                clientId,
            },
        );
        return;
    }

    Promise.resolve(observe.start.call(observe)).catch((error) => {
        logger.warn(
            '[client.runtime-observability.launchdarkly-bootstrap] Failed to start LaunchDarkly browser observability capture; continuing without it',
            {
                error,
                clientId,
            },
        );
    });
};

export const reportLaunchDarklyBrowserIncident = ({
    payload,
    metadata,
    fingerprint,
    suppressionWindowMs,
}: {
    payload: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    fingerprint?: string | null;
    suppressionWindowMs?: number;
}): boolean => {
    const observe = launchDarklyBrowserObserveHandle;
    if (!observe || typeof observe._recordErrorMessage !== 'function') {
        return false;
    }

    const resolvedFingerprint =
        readOptionalString(fingerprint) ??
        readOptionalString(payload.fingerprint) ??
        buildIncidentFingerprint(
            'browser',
            payload.errorCode ?? payload.code,
            payload.errorCategory ?? payload.category,
            payload.route,
            payload.boundary,
            payload.digest,
            payload.message,
        );
    // Dispatcher eligibility is resolved upstream; this helper only handles dedupe and delivery.
    const reportingReservation = reserveLaunchDarklyIncidentReporting({
        fingerprint: resolvedFingerprint,
        suppressionStore: browserIncidentSuppressionStore,
        suppressionWindowMs,
    });
    if (!reportingReservation.shouldReport) {
        return false;
    }

    installObserveUrlRedaction(observe);

    try {
        observe._recordErrorMessage(payload, metadata);
        reportingReservation.commit();
        return true;
    } catch (error) {
        reportingReservation.rollback();
        logger.warn(
            '[client.runtime-observability.launchdarkly-bootstrap] Failed to record browser incident in LaunchDarkly observability; continuing without it',
            {
                error,
                payloadMessage: typeof payload.message === 'string' ? payload.message : undefined,
            },
        );
        return false;
    }
};

export const currentLocationHasSensitiveAuthToken = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    return new URLSearchParams(window.location.search).has(AUTH_TOKEN_PARAM);
};

export const currentLocationIsCockpitRoute = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const { pathname } = window.location;
    return pathname === '/a/cockpit' || pathname.startsWith('/a/cockpit/');
};

const shouldEnableSessionReplay = (sampleRate: number): boolean => {
    if (sampleRate <= 0) {
        return false;
    }

    if (sampleRate >= 1) {
        return true;
    }

    return Math.random() < sampleRate;
};

const resolveContextFriendlyName = ({ clientId, clientName }: { clientId: string; clientName?: string }) => {
    if (typeof clientName !== 'string') {
        return undefined;
    }

    const normalized = clientName.trim();
    return normalized ? () => `client:${normalized}:${clientId}` : undefined;
};

export const startLaunchDarklyBrowserObservability = ({
    clientId,
    clientName,
}: {
    clientId: string;
    clientName?: string;
}): LDClient | null => {
    if (launchDarklyBrowserClient !== undefined) {
        return launchDarklyBrowserClient;
    }

    if (typeof window === 'undefined') {
        launchDarklyBrowserClient = null;
        launchDarklyBrowserObserveHandle = null;
        return launchDarklyBrowserClient;
    }

    const observabilityConfig = publicClientConfig.launchDarklyObservability;
    if (!observabilityConfig.enabled) {
        launchDarklyBrowserClient = null;
        launchDarklyBrowserObserveHandle = null;
        return launchDarklyBrowserClient;
    }

    if (!observabilityConfig.clientSideId) {
        launchDarklyBrowserClient = null;
        launchDarklyBrowserObserveHandle = null;
        logDisabledWarningOnce(
            'LaunchDarkly observability is enabled but NEXT_PUBLIC_LD_CLIENT_SIDE_ID is empty; disabling browser bootstrap',
        );
        return launchDarklyBrowserClient;
    }

    const shouldLimitSensitiveUrlCapture = currentLocationHasSensitiveAuthToken();
    const contextFriendlyName = resolveContextFriendlyName({ clientId, clientName });
    const shouldDisableSessionReplayForRoute = currentLocationIsCockpitRoute();
    const observeOptions: ObserveOptions = {
        serviceName: LD_SERVICE_NAME,
        environment: observabilityConfig.environment,
        version: releaseManifest.revision,
        contextFriendlyName,
        manualStart: true,
        disableConsoleRecording: !observabilityConfig.debug,
        reportConsoleErrors: true,
        enablePerformanceRecording: shouldLimitSensitiveUrlCapture ? false : true,
        tracingOrigins: observabilityConfig.tracesSampleRate > 0 ? true : false,
        productAnalytics: shouldLimitSensitiveUrlCapture
            ? {
                  pageViews: false,
              }
            : true,
        otel: shouldLimitSensitiveUrlCapture
            ? {
                  instrumentations: {
                      '@opentelemetry/instrumentation-document-load': false,
                      '@opentelemetry/instrumentation-user-interaction': false,
                  },
              }
            : undefined,
        networkRecording: {
            enabled: true,
            recordHeadersAndBody: false,
            disableWebSocketEventRecordings: true,
            destinationDomains: [window.location.host],
            requestResponseSanitizer: (pair) => {
                pair.request.url = sanitizeObservedUrl(pair.request.url);
                return pair;
            },
        },
    };

    const observabilityPlugin = new Observability(observeOptions) as LDPlugin & ObservePluginHandle;
    const plugins: LDPlugin[] = [observabilityPlugin];
    if (!shouldDisableSessionReplayForRoute && shouldEnableSessionReplay(observabilityConfig.sessionReplaySampleRate)) {
        installReplayUrlRedaction();
        const sessionReplayOptions: RecordOptions = {
            serviceName: LD_SERVICE_NAME,
            environment: observabilityConfig.environment,
            version: releaseManifest.revision,
            contextFriendlyName,
            privacySetting: 'none',
            disableBackgroundRecording: true,
            inlineImages: false,
        };
        plugins.push(new SessionReplay(sessionReplayOptions));
    }

    try {
        launchDarklyBrowserClient = initialize(
            observabilityConfig.clientSideId,
            {
                kind: 'client',
                key: clientId,
            },
            {
                eventUrlTransformer: sanitizeObservedUrl,
                plugins,
            },
        );
        startObservedCapture(observabilityPlugin, clientId);
    } catch (error) {
        launchDarklyBrowserClient = null;
        launchDarklyBrowserObserveHandle = null;
        logger.warn(
            '[client.runtime-observability.launchdarkly-bootstrap] Failed to initialize LaunchDarkly browser observability; continuing without it',
            {
                error,
                clientId,
            },
        );
    }

    return launchDarklyBrowserClient;
};

export const resetLaunchDarklyBrowserObservabilityForTests = () => {
    launchDarklyBrowserClient = undefined;
    launchDarklyBrowserObserveHandle = null;
    hasLoggedDisabledWarning = false;
    browserIncidentSuppressionStore.reset();
};
